/**
 * Meta Pixel (Madgicx Signals Gateway) — GDPR-gated loader.
 *
 * The pixel script is injected ONLY after the visitor granted marketing
 * consent in the cookie banner. Nothing is sent to Meta before that.
 * Every consented event is also logged (anonymously) to meta_pixel_events
 * so admins can see visitors and bookings per page in /admin/meta-pixel.
 */
import { supabase } from '@/integrations/supabase/client';
import { isSensitiveTrackingPath } from '@/lib/trackingPrivacy';

const PIXEL_ID = '3092127521466923092';
const PIXEL_HOST = 'https://signals.madgicx.cc/';
const SESSION_KEY = 'hac_px_session';
const CONSENT_EXPIRY_MS = 6 * 30 * 24 * 60 * 60 * 1000;

type PixelFn = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[];
  push?: unknown;
  loaded?: boolean;
  version?: string;
};

type PixelWindow = Window & { cbq?: PixelFn; _cbq?: PixelFn; __metaPixelLoaded?: boolean };

export interface MetaUserData {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

/** Returns the ISO timestamp of a valid marketing consent, or null. */
export const getMarketingConsentAt = (): string | null => {
  try {
    const raw = localStorage.getItem('cookie_consent');
    const date = localStorage.getItem('cookie_consent_date');
    if (!raw || !date) return null;
    if (Date.now() - new Date(date).getTime() > CONSENT_EXPIRY_MS) return null;
    const parsed = JSON.parse(raw) as { marketing?: boolean };
    return parsed.marketing === true ? date : null;
  } catch {
    return null;
  }
};

const getSessionId = (): string => {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return crypto.randomUUID();
  }
};

/** Injects the pixel once, only with marketing consent. Returns true when usable. */
export const ensureMetaPixel = (): boolean => {
  if (typeof window === 'undefined' || !getMarketingConsentAt()) return false;
  // Retain the existing integration but pause transmission until dataset eligibility is verified.
  return false;
};

/** Called when the visitor withdraws marketing consent. */
export const revokeMetaPixel = () => {
  const w = window as PixelWindow;
  try {
    w.cbq?.('consent', 'revoke');
  } catch {
    /* ignore */
  }
};

const logEvent = (eventName: string, consentAt: string) => {
  const lang = window.location.pathname.split('/')[1];
  void supabase
    .from('meta_pixel_events')
    .insert({
      event_name: eventName,
      page_path: window.location.pathname.slice(0, 300),
      session_id: getSessionId(),
      language: lang && lang.length <= 5 ? lang : null,
      consent_at: consentAt,
    })
    .then(({ error }) => {
      if (error) console.debug('[meta-pixel] log failed', error.message);
    });
};

const sha256 = async (value: string): Promise<string> => {
  const bytes = new TextEncoder().encode(value);
  const hash = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(hash)).map((b) => b.toString(16).padStart(2, '0')).join('');
};

const buildMatching = async (u: MetaUserData): Promise<Record<string, string>> => {
  const out: Record<string, string> = {};
  const norm = (s?: string) => (s ?? '').trim().toLowerCase();
  if (norm(u.email)) out.em = await sha256(norm(u.email));
  const digits = (u.phone ?? '').replace(/\D/g, '').replace(/^00/, '');
  if (digits) out.ph = await sha256(digits);
  if (norm(u.firstName)) out.fn = await sha256(norm(u.firstName));
  if (norm(u.lastName)) out.ln = await sha256(norm(u.lastName));
  return out;
};

/**
 * Sends a Meta event only when marketing consent exists.
 * Optional userData is SHA-256 hashed before it leaves the browser.
 * Free-text (e.g. the form message) is never sent — it can contain health data.
 */
export const trackMetaEvent = async (
  eventName: string,
  params?: Record<string, unknown>,
  userData?: MetaUserData,
): Promise<boolean> => {
  if (userData || /Lead|Purchase|Schedule|CompleteRegistration|SubmitApplication|ViewContent|Search/i.test(eventName) || isSensitiveTrackingPath(window.location.pathname)) return false;
  const consentAt = getMarketingConsentAt();
  if (!consentAt || !ensureMetaPixel()) return false;
  const w = window as PixelWindow;
  if (userData) {
    const matching = await buildMatching(userData);
    if (Object.keys(matching).length) w.cbq?.('init', PIXEL_ID, matching);
  }
  w.cbq?.('track', eventName, params ?? {});
  logEvent(eventName, consentAt);
  return true;
};

export const trackMetaPageView = () => {
  void trackMetaEvent('PageView');
};
